export type FlowTraceConsentDecision = "denied" | "granted" | "unset";
export type FlowTraceConsentLocale = "en" | "ja" | "yue" | "zh-CN" | "zh-TW";
export type FlowTraceVisibleTextPolicy = "included" | "masked" | "route-dependent";

export interface FlowTraceRecorderPrivacyOptions {
  maskAllText: boolean;
}

/**
 * "paper" is the default light bar. "wire" is a compact dark card with a 1px
 * accent outline, centered at the bottom, for dark sites where the bar stands out.
 */
export type FlowTraceConsentAppearance = "paper" | "wire";

export interface FlowTraceConsentOptions {
  /** Any CSS color; used for the outline, kicker, and allow button of "wire". */
  accent?: string;
  additionalAnalytics?: string[];
  appearance?: FlowTraceConsentAppearance;
  appId: string;
  container?: HTMLElement;
  disclosureBaseUrl?: string;
  excludedSurfaces?: string[];
  initialDecision?: FlowTraceConsentDecision;
  locale?: FlowTraceConsentLocale;
  onDecision?(
    decision: Exclude<FlowTraceConsentDecision, "unset">,
    recorderPrivacy: FlowTraceRecorderPrivacyOptions,
  ): void | Promise<void>;
  persist?: boolean;
  privacyPolicyUrl?: string;
  projectName?: string;
  storageKey?: string;
  visibleText?: FlowTraceVisibleTextPolicy;
}

export interface FlowTraceConsentController {
  destroy(): void;
  getDecision(): FlowTraceConsentDecision;
  open(): void;
  setDecision(decision: Exclude<FlowTraceConsentDecision, "unset">): void;
}

interface ConsentCopy {
  allow: string;
  analyticsPrefix: string;
  close: string;
  collected: string;
  collectedItems: string[];
  deniedStatus: string;
  details: string;
  disclosure: string;
  excludedPrefix: string;
  grantedStatus: string;
  inputsProtected: string;
  intro: string;
  kicker: string;
  listSeparator: string;
  privacy: string;
  projectIntro: (projectName: string) => string;
  protectedPrefix: string;
  reject: string;
  textIncluded: string;
  textMasked: string;
  textRouteDependent: string;
  title: string;
}

const DEFAULT_DISCLOSURE_BASE_URL = "https://flowtrace.nex-a.net/disclosures";
const EVENT_NAME = "flowtrace:consent-changed";

const copy: Record<FlowTraceConsentLocale, ConsentCopy> = {
  ja: {
    allow: "はい",
    analyticsPrefix: "この選択は次の解析にも適用されます:",
    close: "閉じる",
    collected: "FlowTraceで確認すること",
    collectedItems: [
      "ページ遷移と表示ルート",
      "クリック、スクロール、画面内の変化",
      "ブラウザエラーと安定したUIイベント",
    ],
    deniedStatus: "現在は記録を許可していません。",
    details: "詳細",
    disclosure: "FlowTraceの利用内容",
    excludedPrefix: "記録対象外:",
    grantedStatus: "現在は匿名の行動分析を許可しています。",
    inputsProtected: "入力値、Cookie、Authorization、通信本文は収集しません。",
    intro: "サイト改善のためNexA FlowTraceを利用します。記録は許可後に始まります。",
    kicker: "FLOWTRACE",
    listSeparator: "、",
    privacy: "プライバシーポリシー",
    projectIntro: (projectName) =>
      `${projectName}では、サイト改善のためNexA FlowTraceを利用します。記録は許可後に始まります。`,
    protectedPrefix: "保護: ",
    reject: "いいえ",
    textIncluded: "公開ページの表示テキストはリプレイに含まれます。",
    textMasked: "ページ上の表示テキストはマスクして記録します。",
    textRouteDependent: "指定した公開ページの表示テキストだけを含め、その他のページではマスクします。",
    title: "サイト改善のため、匿名の利用状況を取得してもよいですか？",
  },
  en: {
    allow: "Yes",
    analyticsPrefix: "This choice also applies to:",
    close: "Close",
    collected: "What FlowTrace helps us understand",
    collectedItems: [
      "Page changes and displayed routes",
      "Clicks, scrolling, and visual changes",
      "Browser errors and stable UI events",
    ],
    deniedStatus: "Experience recording is currently declined.",
    details: "Details",
    disclosure: "How this site uses FlowTrace",
    excludedPrefix: "Excluded from recording:",
    grantedStatus: "Anonymous experience analytics is currently allowed.",
    inputsProtected: "Input values, cookies, Authorization headers, and network bodies are not collected.",
    intro: "This site uses NexA FlowTrace to improve its experience. Recording starts only after you allow it.",
    kicker: "FLOWTRACE",
    listSeparator: ", ",
    privacy: "Privacy policy",
    projectIntro: (projectName) =>
      `${projectName} uses NexA FlowTrace to improve this site. Recording starts only after you allow it.`,
    protectedPrefix: "Protected: ",
    reject: "No",
    textIncluded: "Visible text on public pages is included in replay.",
    textMasked: "Visible page text is masked in replay.",
    textRouteDependent: "Visible text is included only on declared public pages and masked elsewhere.",
    title: "May we collect anonymous usage data to improve this site?",
  },
  "zh-CN": {
    allow: "可以",
    analyticsPrefix: "此选择也适用于以下分析工具:",
    close: "关闭",
    collected: "FlowTrace 帮助我们了解的内容",
    collectedItems: [
      "页面切换和显示路由",
      "点击、滚动和画面变化",
      "浏览器错误和稳定的界面事件",
    ],
    deniedStatus: "当前未允许体验记录。",
    details: "详情",
    disclosure: "FlowTrace 使用说明",
    excludedPrefix: "不记录:",
    grantedStatus: "当前已允许匿名体验分析。",
    inputsProtected: "不收集输入值、Cookie、Authorization 标头或网络正文。",
    intro: "本网站使用 NexA FlowTrace 改善体验。仅在您允许后开始记录。",
    kicker: "FLOWTRACE",
    listSeparator: "、",
    privacy: "网站隐私政策",
    projectIntro: (projectName) =>
      `${projectName} 使用 NexA FlowTrace 改善网站体验。仅在您允许后开始记录。`,
    protectedPrefix: "保护: ",
    reject: "不可以",
    textIncluded: "公共页面的可见文本会包含在回放中。",
    textMasked: "页面上的可见文本会经过遮罩后记录。",
    textRouteDependent: "仅指定公共页面的可见文本会包含在回放中，其他页面会进行遮罩。",
    title: "为改善网站，可以收集匿名使用情况吗？",
  },
  "zh-TW": {
    allow: "可以",
    analyticsPrefix: "此選擇也適用於以下分析工具:",
    close: "關閉",
    collected: "FlowTrace 協助我們了解的內容",
    collectedItems: [
      "頁面切換和顯示路由",
      "點擊、捲動和畫面變化",
      "瀏覽器錯誤和穩定的介面事件",
    ],
    deniedStatus: "目前未允許體驗記錄。",
    details: "詳情",
    disclosure: "FlowTrace 使用說明",
    excludedPrefix: "不記錄:",
    grantedStatus: "目前已允許匿名體驗分析。",
    inputsProtected: "不收集輸入值、Cookie、Authorization 標頭或網路本文。",
    intro: "本網站使用 NexA FlowTrace 改善體驗。只會在你允許後開始記錄。",
    kicker: "FLOWTRACE",
    listSeparator: "、",
    privacy: "網站隱私權政策",
    projectIntro: (projectName) =>
      `${projectName} 使用 NexA FlowTrace 改善網站體驗。只會在你允許後開始記錄。`,
    protectedPrefix: "保護: ",
    reject: "不可以",
    textIncluded: "公開頁面的可見文字會包含在重播中。",
    textMasked: "頁面上的可見文字會經過遮罩後記錄。",
    textRouteDependent: "只有指定公開頁面的可見文字會包含在重播中，其他頁面會進行遮罩。",
    title: "為改善網站，可以收集匿名使用情況嗎？",
  },
  yue: {
    allow: "可以",
    analyticsPrefix: "呢個選擇亦適用於以下分析工具:",
    close: "關閉",
    collected: "FlowTrace 幫我哋了解嘅內容",
    collectedItems: [
      "頁面切換同顯示路由",
      "點擊、捲動同畫面變化",
      "瀏覽器錯誤同穩定嘅介面事件",
    ],
    deniedStatus: "目前未允許體驗記錄。",
    details: "詳情",
    disclosure: "FlowTrace 使用說明",
    excludedPrefix: "唔會記錄:",
    grantedStatus: "目前已允許匿名體驗分析。",
    inputsProtected: "唔會收集輸入值、Cookie、Authorization 標頭或網絡正文。",
    intro: "呢個網站使用 NexA FlowTrace 改善體驗，只會喺你允許後開始記錄。",
    kicker: "FLOWTRACE",
    listSeparator: "、",
    privacy: "網站私隱政策",
    projectIntro: (projectName) =>
      `${projectName} 使用 NexA FlowTrace 改善網站體驗，只會喺你允許後開始記錄。`,
    protectedPrefix: "保護: ",
    reject: "唔可以",
    textIncluded: "公開頁面嘅可見文字會包含喺重播入面。",
    textMasked: "頁面上嘅可見文字會經過遮罩後記錄。",
    textRouteDependent: "只會喺指定公開頁面記錄可見文字，其他頁面會遮罩文字。",
    title: "為改善網站，可以收集匿名使用情況嗎？",
  },
};

const normalizeList = (values: string[] | undefined): string[] =>
  Array.from(
    new Set(
      (values ?? [])
        .map((value) => value.trim().replace(/\s+/g, " ").slice(0, 80))
        .filter(Boolean),
    ),
  ).slice(0, 8);

export const resolveFlowTraceConsentLocale = (
  locale: string | null | undefined,
): FlowTraceConsentLocale => {
  const normalized = locale?.trim().toLowerCase() ?? "";
  if (normalized.startsWith("ja")) return "ja";
  if (normalized.startsWith("yue") || normalized.startsWith("zh-hk")) return "yue";
  if (normalized.startsWith("zh-hant") || normalized.startsWith("zh-tw")) return "zh-TW";
  if (normalized.startsWith("zh")) return "zh-CN";
  return "en";
};

export const createFlowTraceDisclosureUrl = (
  appId: string,
  locale: FlowTraceConsentLocale,
  baseUrl = DEFAULT_DISCLOSURE_BASE_URL,
): string => {
  const normalizedBase = baseUrl.replace(/\/+$/, "");
  const url = new URL(`${normalizedBase}/${encodeURIComponent(appId)}`);
  url.searchParams.set("lang", locale);
  return url.toString();
};

export const createFlowTraceRecorderPrivacyOptions = (
  visibleText: FlowTraceVisibleTextPolicy = "included",
): FlowTraceRecorderPrivacyOptions => ({
  maskAllText: visibleText === "masked",
});

const getVisibleTextCopy = (
  currentCopy: ConsentCopy,
  visibleText: FlowTraceVisibleTextPolicy | undefined,
): string => {
  if (visibleText === "masked") return currentCopy.textMasked;
  if (visibleText === "route-dependent") return currentCopy.textRouteDependent;
  return currentCopy.textIncluded;
};

const readStoredDecision = (storageKey: string): FlowTraceConsentDecision => {
  try {
    const stored = window.localStorage.getItem(storageKey);
    return stored === "granted" || stored === "denied" ? stored : "unset";
  } catch {
    return "unset";
  }
};

const writeStoredDecision = (
  storageKey: string,
  decision: Exclude<FlowTraceConsentDecision, "unset">,
): void => {
  try {
    window.localStorage.setItem(storageKey, decision);
  } catch {
    // The control remains functional when storage is unavailable.
  }
};

const appendText = (
  parent: ParentNode,
  tagName: keyof HTMLElementTagNameMap,
  text: string,
  className?: string,
): HTMLElement => {
  const element = document.createElement(tagName);
  element.textContent = text;
  if (className) element.className = className;
  parent.append(element);
  return element;
};

const CONSENT_STYLES = `
  :host { color-scheme: light; font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
  * { box-sizing: border-box; }
  button, a { font: inherit; }
  .panel { position: fixed; z-index: 2147483000; left: 50%; bottom: calc(max(10px, env(safe-area-inset-bottom)) + var(--flowtrace-consent-bottom-offset, 0px)); width: min(920px, calc(100vw - 24px)); border: 1px solid rgba(44, 37, 30, .16); border-radius: 14px; background: rgba(255, 253, 249, .97); color: #241f1a; box-shadow: 0 12px 36px rgba(45, 37, 29, .17); transform: translateX(-50%); backdrop-filter: blur(16px); }
  .panel[hidden] { display: none; }
  .body { position: relative; padding: 9px 10px 9px 14px; }
  .primary { display: flex; align-items: center; gap: 14px; min-width: 0; }
  .prompt { display: flex; flex: 1; align-items: center; gap: 10px; min-width: 0; }
  .kicker { flex: none; margin: 0; color: #9b482b; font-size: 9px; font-weight: 800; letter-spacing: .12em; }
  h2 { margin: 0; min-width: 0; font-size: 14px; font-weight: 680; line-height: 1.45; letter-spacing: -.01em; }
  .intro { margin: 0 0 13px; color: #655d55; font-size: 13px; line-height: 1.6; }
  details { margin: 0; }
  summary { display: inline-flex; align-items: center; justify-content: center; min-height: 34px; border: 1px solid #d8cec4; border-radius: 8px; padding: 7px 11px; background: #fffdf9; color: #4d453e; cursor: pointer; font-size: 12px; font-weight: 680; list-style: none; }
  summary::-webkit-details-marker { display: none; }
  details[open] summary { border-color: #b98570; background: #fff7f1; color: #783b27; }
  .detail { position: absolute; right: 0; bottom: calc(100% + 8px); left: 0; max-height: min(520px, calc(100vh - 88px)); overflow: auto; border: 1px solid rgba(44, 37, 30, .16); border-radius: 14px; padding: 17px 18px; background: rgba(255, 253, 249, .99); box-shadow: 0 16px 46px rgba(45, 37, 29, .2); }
  .detail h3 { margin: 0 0 7px; font-size: 12px; }
  ul { margin: 0 0 10px; padding-left: 18px; color: #5f5750; font-size: 12px; line-height: 1.55; }
  .boundary, .extra { margin: 8px 0 0; color: #5f5750; font-size: 12px; line-height: 1.55; }
  .boundary strong { color: #332d27; }
  .status { margin: 12px 0 0; color: #685f57; font-size: 12px; }
  .links { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 14px; }
  .links a { color: #8b4028; font-size: 12px; font-weight: 680; text-underline-offset: 3px; }
  .actions { display: flex; flex: none; align-items: center; gap: 6px; }
  .actions button { min-width: 54px; min-height: 34px; border-radius: 8px; padding: 7px 12px; cursor: pointer; font-size: 12px; }
  .allow { border: 1px solid #a74828; background: #ad4b2c; color: white; font-weight: 740; box-shadow: 0 4px 10px rgba(155, 67, 37, .15); }
  .deny, .close { border: 1px solid #d8cec4; background: #fffdf9; color: #4d453e; }
  button:focus-visible, a:focus-visible, summary:focus-visible { outline: 3px solid rgba(210, 91, 48, .38); outline-offset: 3px; }
  @media (max-width: 680px) { .panel { bottom: calc(max(8px, env(safe-area-inset-bottom)) + var(--flowtrace-consent-bottom-offset, 0px)); width: calc(100vw - 16px); } .body { padding: 10px; } .primary { flex-wrap: wrap; gap: 8px; } .prompt { flex-basis: 100%; gap: 8px; } h2 { font-size: 13px; } .actions { width: 100%; justify-content: flex-end; } .detail { max-height: calc(100vh - 118px); padding: 15px; } }
  @media (prefers-reduced-motion: no-preference) { .panel:not([hidden]) { animation: enter .18s ease-out both; } @keyframes enter { from { opacity: 0; transform: translate(-50%, 6px); } to { opacity: 1; transform: translate(-50%, 0); } } }

  :host([data-appearance="wire"]) { color-scheme: dark; --ft-accent: #4da3ff; --ft-line: rgba(234, 244, 255, .16); --ft-text: #eaf4ff; --ft-muted: rgba(234, 244, 255, .62); }
  .wire.panel { bottom: calc(max(12px, env(safe-area-inset-bottom)) + var(--flowtrace-consent-bottom-offset, 0px)); width: min(640px, calc(100vw - 24px)); border: 1px solid color-mix(in srgb, var(--ft-accent) 55%, transparent); border-radius: 4px; background: rgba(3, 9, 18, .78); color: var(--ft-text); box-shadow: none; }
  .wire .body { padding: 8px 8px 8px 14px; }
  .wire .primary { gap: 12px; }
  .wire .prompt { align-items: baseline; gap: 10px; }
  .wire .kicker { color: var(--ft-accent); }
  .wire h2 { font-size: 13px; font-weight: 500; line-height: 1.5; }
  .wire .actions { gap: 2px; }
  .wire .actions button, .wire summary { min-width: 0; min-height: 30px; border: 1px solid transparent; border-radius: 3px; padding: 4px 10px; background: transparent; box-shadow: none; color: var(--ft-muted); font-weight: 500; letter-spacing: .02em; transition: color .15s, background-color .15s, border-color .15s; }
  .wire .actions button:hover, .wire summary:hover { color: var(--ft-text); }
  .wire .allow { border-color: color-mix(in srgb, var(--ft-accent) 70%, transparent); background: color-mix(in srgb, var(--ft-accent) 12%, transparent); color: var(--ft-text); font-weight: 600; padding: 4px 16px; }
  .wire .allow:hover { background: color-mix(in srgb, var(--ft-accent) 24%, transparent); }
  .wire details[open] summary { color: var(--ft-accent); }
  .wire .detail { border: 1px solid color-mix(in srgb, var(--ft-accent) 40%, transparent); border-radius: 4px; background: rgba(3, 9, 18, .96); color: var(--ft-text); box-shadow: none; }
  .wire .intro, .wire ul, .wire .boundary, .wire .extra, .wire .status { color: var(--ft-muted); }
  .wire .boundary strong { color: var(--ft-text); }
  .wire .links a { color: var(--ft-accent); }
  .wire button:focus-visible, .wire a:focus-visible, .wire summary:focus-visible { outline: 2px solid var(--ft-accent); outline-offset: 2px; }
  @media (max-width: 680px) { .wire.panel { width: calc(100vw - 16px); } .wire .prompt { flex-wrap: wrap; gap: 2px 10px; } }
`;

export const mountFlowTraceConsent = (
  options: FlowTraceConsentOptions,
): FlowTraceConsentController => {
  if (!options.appId.trim()) throw new Error("FlowTrace consent requires appId");

  const locale = resolveFlowTraceConsentLocale(
    options.locale ?? window.navigator.language,
  );
  const currentCopy = copy[locale];
  const storageKey = options.storageKey ?? `flowtrace-consent:${options.appId}`;
  const shouldPersist = options.persist !== false;
  const additionalAnalytics = normalizeList(options.additionalAnalytics);
  const excludedSurfaces = normalizeList(options.excludedSurfaces);
  const recorderPrivacy = createFlowTraceRecorderPrivacyOptions(options.visibleText);
  const projectName = options.projectName?.trim().slice(0, 120);
  const host = document.createElement("div");
  host.dataset.flowtraceConsent = options.appId;
  const appearance = options.appearance === "wire" ? "wire" : "paper";
  host.dataset.appearance = appearance;
  const accent = options.accent?.trim();
  if (appearance === "wire" && accent && CSS.supports("color", accent)) {
    host.style.setProperty("--ft-accent", accent);
  }
  const shadow = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = CONSENT_STYLES;
  shadow.append(style);

  const panel = document.createElement("section");
  panel.className = appearance === "wire" ? "panel wire" : "panel";
  panel.setAttribute("aria-labelledby", "flowtrace-consent-title");
  panel.setAttribute("aria-modal", "false");
  panel.setAttribute("role", "dialog");
  const body = document.createElement("div");
  body.className = "body";
  panel.append(body);
  const primary = document.createElement("div");
  primary.className = "primary";
  body.append(primary);
  const prompt = document.createElement("div");
  prompt.className = "prompt";
  primary.append(prompt);
  appendText(prompt, "p", currentCopy.kicker, "kicker");
  const title = appendText(prompt, "h2", currentCopy.title);
  title.id = "flowtrace-consent-title";

  const details = document.createElement("details");
  const summary = document.createElement("summary");
  summary.textContent = currentCopy.details;
  details.append(summary);
  const detail = document.createElement("div");
  detail.className = "detail";
  details.append(detail);
  appendText(
    detail,
    "p",
    projectName ? currentCopy.projectIntro(projectName) : currentCopy.intro,
    "intro",
  );
  appendText(detail, "h3", currentCopy.collected);
  const list = document.createElement("ul");
  for (const item of currentCopy.collectedItems) appendText(list, "li", item);
  detail.append(list);
  appendText(
    detail,
    "p",
    getVisibleTextCopy(currentCopy, options.visibleText),
    "boundary",
  );
  const protection = appendText(detail, "p", currentCopy.inputsProtected, "boundary");
  const protectionStrong = document.createElement("strong");
  protectionStrong.textContent = currentCopy.protectedPrefix;
  protection.prepend(protectionStrong);
  if (excludedSurfaces.length > 0) {
    appendText(
      detail,
      "p",
      `${currentCopy.excludedPrefix} ${excludedSurfaces.join(currentCopy.listSeparator)}`,
      "boundary",
    );
  }
  if (additionalAnalytics.length > 0) {
    appendText(
      detail,
      "p",
      `${currentCopy.analyticsPrefix} ${additionalAnalytics.join(", ")}`,
      "extra",
    );
  }
  const status = appendText(detail, "p", "", "status");
  status.setAttribute("aria-live", "polite");
  const links = document.createElement("div");
  links.className = "links";
  const disclosureLink = document.createElement("a");
  disclosureLink.href = createFlowTraceDisclosureUrl(
    options.appId,
    locale,
    options.disclosureBaseUrl,
  );
  disclosureLink.rel = "noreferrer";
  disclosureLink.target = "_blank";
  disclosureLink.textContent = currentCopy.disclosure;
  links.append(disclosureLink);
  if (options.privacyPolicyUrl) {
    const privacyLink = document.createElement("a");
    privacyLink.href = options.privacyPolicyUrl;
    privacyLink.textContent = currentCopy.privacy;
    links.append(privacyLink);
  }
  detail.append(links);

  const actions = document.createElement("div");
  actions.className = "actions";
  const allow = appendText(actions, "button", currentCopy.allow, "allow") as HTMLButtonElement;
  allow.type = "button";
  const deny = appendText(actions, "button", currentCopy.reject, "deny") as HTMLButtonElement;
  deny.type = "button";
  actions.append(details);
  const close = appendText(actions, "button", currentCopy.close, "close") as HTMLButtonElement;
  close.type = "button";
  primary.append(actions);
  shadow.append(panel);

  let decision = options.initialDecision ?? (shouldPersist ? readStoredDecision(storageKey) : "unset");
  let isOpen = decision === "unset";

  const render = () => {
    host.hidden = !isOpen;
    panel.hidden = !isOpen;
    close.hidden = decision === "unset";
    status.hidden = decision === "unset";
    status.textContent = decision === "granted"
      ? currentCopy.grantedStatus
      : decision === "denied"
        ? currentCopy.deniedStatus
        : "";
  };

  const notifyDecision = (
    nextDecision: Exclude<FlowTraceConsentDecision, "unset">,
  ) => {
    if (!options.onDecision) return;
    void Promise.resolve(options.onDecision(nextDecision, recorderPrivacy)).catch((error) => {
      console.error("[flowtrace-consent] onDecision failed", error);
    });
  };

  const setDecision = (nextDecision: Exclude<FlowTraceConsentDecision, "unset">) => {
    decision = nextDecision;
    isOpen = false;
    details.open = false;
    if (shouldPersist) writeStoredDecision(storageKey, nextDecision);
    render();
    window.dispatchEvent(
      new CustomEvent(EVENT_NAME, {
        detail: { appId: options.appId, decision: nextDecision },
      }),
    );
    notifyDecision(nextDecision);
  };

  allow.addEventListener("click", () => setDecision("granted"));
  deny.addEventListener("click", () => setDecision("denied"));
  close.addEventListener("click", () => {
    isOpen = false;
    details.open = false;
    render();
  });
  (options.container ?? document.body).append(host);
  render();
  if (decision !== "unset") notifyDecision(decision);

  return {
    destroy() {
      host.remove();
    },
    getDecision() {
      return decision;
    },
    open() {
      isOpen = true;
      render();
    },
    setDecision,
  };
};
